import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for entering a game
export const ENTER_GAME_MUTATION = gql`
  mutation EnterGame($input: EnterGameInput!) {
    enterGame(input: $input) {
      success
      message
      sessionToken
      launchUrl
      balance
      currency
    }
  }
`;

// Types for the input
export interface EnterGameInput {
  gameId: string;
  userId: number;
  redirectUrl: string;
  providerId: number;
}

// Types for the response
export interface EnterGameResponse {
  success: boolean;
  message?: string;
  sessionToken?: string;
  launchUrl?: string;
  balance?: number;
  currency?: string;
}

// Raw GraphQL response interface
export interface RawEnterGameResponse {
  enterGame: EnterGameResponse;
}

// EnterToGame service class
export class EnterToGameService {
  private static instance: EnterToGameService;

  private constructor() {}

  public static getInstance(): EnterToGameService {
    if (!EnterToGameService.instance) {
      EnterToGameService.instance = new EnterToGameService();
    }
    return EnterToGameService.instance;
  }

  // Enter a game with the provided parameters
  async enterGame(input: EnterGameInput): Promise<EnterGameResponse> {
    try {
      const { data, errors } = await apolloClient.mutate<RawEnterGameResponse>({
        mutation: ENTER_GAME_MUTATION,
        variables: {
          input,
        },
      });

      if (data?.enterGame) {
        return data.enterGame;
      }

      // If there are errors in the response, throw the first error message
      if (errors && errors.length > 0) {
        const messages = errors.map((err: any) => err.message).join('\n');
        throw new Error(messages);
      }

      throw new Error('No data received from enterGame mutation');
    } catch (error: any) {
      // ApolloError: error.graphQLErrors, error.networkError, etc.
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const messages = error.graphQLErrors
          .map((err: any) => err.message)
          .join('\n');
        throw new Error(messages);
      }
      if (error.networkError) {
        throw new Error('Network Error: ' + error.networkError.message);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const enterToGameService = EnterToGameService.getInstance();

// Export convenience function
export async function enterGame(
  input: EnterGameInput,
): Promise<EnterGameResponse> {
  return enterToGameService.enterGame(input);
}
